import {
  EquipmentModel,
  InspectionModel,
  ObservationModel,
} from '../../data/types';
import { inspectionDB } from '../../data/database.structure';
import {
  ResponseError,
  SaveInspectionsOperationRequest,
} from '../../api-contracts';
import { ApiFactory } from '../api';
import DatabaseAssetsMethods from '../../data/database.assets.methods';
import DatabaseInspectionsMethods from '../../data/database.inspections.methods';
import DatabaseEquipmentPartsMethods from '../../data/database.equipment-parts.methods';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import {
  buildGetChangeOutSyncItems,
  buildUndercarriageChangeOutSyncItems,
} from '../../helpers/buildChangeOutSyncItems';
import observationImagesHelpers from '../../helpers/observationImagesHelpers';
import { reconcileEquipmentServerIds } from '../../helpers/reconcileInspectionServerIds';
import { resolveSubmitEntityId } from '../../helpers/inspectionSubmitHelpers';
import {
  buildInvalidEquipmentSubmitMessage,
  findInvalidEquipmentAssignments,
} from '../../helpers/equipmentAssignmentHelpers';
import { InspectionModelHelpers } from '../../helpers/inspection-model-helpers';
import { isCountedFailure } from '../../helpers/sendFailureHelpers';
import ObservationHelpers from '../../helpers/observationHelpers';
import { inspectionRecordService } from '../inspection/inspectionRecordService';
import { updateService } from '../updateService';
import { observationDraftService } from '../observationDraftService';
import AzureTokenPayloadService from '../azureTokenPayloadService';
import { queryClient } from '../../clients/queryClient';

import {
  InspectionPushService,
  PushCallbacks,
  PushOutcome,
} from './inspectionPushCore';

export type { PushCallbacks, PushOutcome } from './inspectionPushCore';
export { InspectionPushService } from './inspectionPushCore';

/**
 * The payload is AUTHORITATIVE: the server deletes any of its observations absent
 * from it (delete-by-omission diff in InspectionSyncRepository). That makes two
 * wire-level guards safe and self-cleaning:
 *  - empty-string non-collection values are dropped - '' is never data in this app
 *    (the draft layer tombstones it); omitting legacy junk rows also deletes the
 *    server's historic copies of them on the next push (RT-E found 39).
 *  - duplicate non-collection tuples keep only the FIRST occurrence, because that
 *    is the row every read and draft-flush targets (`find(matches)`): the app
 *    displays and edits the first row, so the wire must carry it. (Self-review
 *    caught keep-last here: the draft flush updates duplicates IN PLACE via
 *    find-first, so keep-last shipped the stale twin of a just-edited field and
 *    delete-by-omission then erased the edited row server-side.)
 * Exported for the golden payload tests.
 */
export function sanitizeObservationsForWire(
  observations: ObservationModel[],
): ObservationModel[] {
  // '' is never data - for collections either (an empty photo filename or gauge
  // reading is junk that can only mislead downstream consumers).
  return ObservationHelpers.keepFirstRowPerField(
    observations.filter((obs) => obs.value !== ''),
  );
}

// Bucket creation rewrites the observation objects it is given in place.
const copyObservations = (inspection: InspectionModel): ObservationModel[] =>
  inspection.inspectionCategoryTypes[0].observations.map((o) => ({ ...o }));

/**
 * The inspection JSON push, extracted from the useInspectionSync hook so it is callable
 * OUTSIDE React (the global sync drain). The hook wraps this service; both the submit
 * screen and the drain share one path, and the per-externalId in-flight dedupe (see
 * inspectionPushCore) means concurrent callers share a single push instead of
 * double-posting.
 *
 * One push attempt. A failure is recorded on the Inspection, so background sync waits
 * before the next one and stops after 3 that retrying won't fix (#11590, SY-6). */
export async function pushInspectionOnce(
  externalId: string,
  callbacks?: PushCallbacks,
): Promise<PushOutcome> {
  const outcome = await attemptPush(externalId, callbacks);
  if (outcome.failure) {
    await inspectionRecordService.recordSendFailure(
      externalId,
      outcome.failure,
    );
  }
  return outcome;
}

const failed = (
  reason: string,
  { counted = false, fatal = false } = {},
): PushOutcome => ({ inspection: null, failure: { counted, reason }, fatal });

/**
 * Sends the stored Inspection once and applies the result to the current record. A
 * failure is returned, never thrown; `pushInspectionOnce` records it.
 */
async function attemptPush(
  externalId: string,
  callbacks?: PushCallbacks,
): Promise<PushOutcome> {
  try {
    // Persist any pending observation drafts BEFORE reading the record - the
    // payload must never lag behind what the user typed (throws on failure so
    // a sync can't silently run on stale data).
    await observationDraftService.flushAll();

    const inspectionMethods = new DatabaseInspectionsMethods(inspectionDB);
    const initialInspection = await inspectionDB.inspections
      .where('externalId')
      .equals(externalId)
      .first();
    if (!initialInspection) {
      return failed('Inspection not found');
    }
    callbacks?.onInspectionLoaded?.(initialInspection);

    // #10006: shell inspections synced without category types have nothing to
    // submit - fail up front instead of throwing on inspectionCategoryTypes[0] below.
    if (!InspectionModelHelpers.hasCategoryTypes(initialInspection)) {
      return failed(
        'This inspection has no inspection categories and cannot be submitted.',
      );
    }

    // Convert any HEIC images to JPEG before sync. A photo missing from the cache is
    // left to the missing-image guard, which runs after the server refresh (#11965).
    const CONVERSION_TIMEOUT_MS = 300000;
    const heicObservations: ObservationModel[] = (
      await observationImagesHelpers.getUnsyncedImageObservations(
        initialInspection.inspectionCategoryTypes.flatMap(
          (categoryType) => categoryType.observations,
        ),
      )
    ).filter((o) => o.hasImage && !o.fileReceived);

    if (heicObservations.length > 0) {
      // heicObservations are copies (from getUnsyncedImageObservations), so the conversion's in-place
      // rename updates the copy and the cache; the stored Inspection is renamed via applyPhotoRenames below.
      const valuesBeforeConversion = heicObservations.map((o) => o.value);
      const conversionPromises = heicObservations.map((observation) =>
        observationImagesHelpers.convertAndUpdateHEICImageInCache(observation),
      );
      const timeoutPromise = new Promise<'timeout'>((resolve) =>
        setTimeout(() => resolve('timeout'), CONVERSION_TIMEOUT_MS),
      );
      const conversionRace = await Promise.race([
        Promise.all(conversionPromises).then((results) => ({ results })),
        timeoutPromise,
      ]);

      const formatObservationName = (o: ObservationModel) =>
        `- ${o.observableItem.label || o.observableItem.identifier.replace('.', ' > ')}`;

      if (conversionRace === 'timeout') {
        const failedList = heicObservations
          .map(formatObservationName)
          .join('\n');
        const errorMsg = `Image conversion timed out. The following images could not be converted from HEIC format:\n${failedList}\n\nPlease retake these photos in JPEG format and try again.`;
        return failed(errorMsg);
      }

      const failedConversions = heicObservations.filter(
        (_, i) => !conversionRace.results[i],
      );
      if (failedConversions.length > 0) {
        const failedList = failedConversions
          .map(formatObservationName)
          .join('\n');
        const errorMsg = `Failed to convert HEIC images to JPEG:\n${failedList}\n\nPlease retake these photos in JPEG format and try again.`;
        return failed(errorMsg);
      }

      await inspectionRecordService.applyPhotoRenames(
        externalId,
        heicObservations
          .map((o, i) => ({
            observationExternalId: o.externalId,
            from: valuesBeforeConversion[i],
            to: o.value,
          }))
          .filter((rename) => rename.from !== rename.to),
      );
    }

    const api = ApiFactory.getInspectionApi();

    // Pre-submit refresh (#9416): pull the server's current copy of this inspection so
    // stale local ids can't re-create entities the server already has. A 404 means the
    // inspection is new to the server; any other failure falls back to the local copy.
    try {
      const refreshResponse = await api.getInspectionByExternalId({
        externalId,
      });
      if (refreshResponse.syncItems) {
        await inspectionRecordService.applyServerIds(
          externalId,
          refreshResponse.syncItems,
        );
      }
      await reconcileEquipmentServerIds(refreshResponse);
    } catch (refreshError) {
      const isNotFound =
        refreshError instanceof ResponseError &&
        refreshError.response.status === 404;
      if (!isNotFound) {
        console.warn(
          'Pre-submit inspection refresh failed, continuing with local data:',
          refreshError,
        );
      }
    }

    const assetMethods = new DatabaseAssetsMethods(inspectionDB);
    const equipmentPartsMethods = new DatabaseEquipmentPartsMethods(
      inspectionDB,
    );

    const inspectionViewModel =
      await inspectionMethods.getInspectionByExternalId(externalId);
    if (!inspectionViewModel) {
      return failed('Inspection not found locally');
    }

    let data = inspectionViewModel.inspection;

    // At the last moment, check the inspector name and update if "Unknown User"
    if (data.inspectorName === 'Unknown User' || !data.inspectorName) {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        data.inspectorName = AzureTokenPayloadService.getFullNameOfUser(token);
        await inspectionRecordService.setInspectorName(
          data.externalId,
          data.inspectorName,
        );
      }
    }

    const missingImages =
      await observationImagesHelpers.getUnsyncedImageObservations(
        data.inspectionCategoryTypes.flatMap(
          (categoryType) => categoryType.observations,
        ),
      );

    if (missingImages.some((o) => o.hasImage === false)) {
      const missingList = missingImages
        .filter((o) => o.hasImage === false)
        .map((o) => `- ${o.observableItem.identifier.replace('.', ' > ')}`);
      const errorMsg = `One or more images are missing from cache: \n ${missingList.join('\n')}\n Please check the images and try again.`;
      return failed(errorMsg, { counted: true });
    }

    const currentBucket = await assetMethods.getInspectionAssetCurrentEquipment(
      {
        inspectionId: data.externalId,
      },
    );

    const equipmentList: EquipmentModel[] = [];

    // ---- Handle bucket changeout logic ----
    if (currentBucket.isChanged && currentBucket.bucket) {
      const changeoutFromObservation =
        data.inspectionCategoryTypes[0].observations.find(
          (ob) =>
            ob.observableItem.identifier ===
            ObservableItemIdentifier.Identifiers.Bucket.ChangeOutFrom,
        );

      if (changeoutFromObservation) {
        let changeoutToBucket = await assetMethods.joinBucketObservationData({
          bucket: currentBucket.bucket,
          observations: data.inspectionCategoryTypes[0].observations,
        });

        if (currentBucket.bucket.id < 1) {
          const observationsBefore = copyObservations(data);
          const response =
            await assetMethods.createNewBucketFromEquipmentAndObservations({
              bucket: changeoutToBucket,
              observations: data.inspectionCategoryTypes[0].observations,
              effectiveDate: data.inspectionDate,
            });
          changeoutToBucket = response.bucket;
          data = await inspectionDB.transaction(
            'rw',
            inspectionDB.inspections,
            inspectionDB.inspectionBackups,
            inspectionDB.equipmentParts,
            async () => {
              const updated =
                await inspectionRecordService.applyObservationRewrite(
                  externalId,
                  observationsBefore,
                  response.observations,
                );
              await equipmentPartsMethods.replaceEquipmentEquipmentParts({
                equipmentExternalId: response.bucket.externalId,
                equipmentParts: response.bucket.equipmentParts,
              });
              return updated;
            },
          );
        } else {
          changeoutToBucket =
            await assetMethods.joinBucketEquipmentPartsObservationData({
              bucket: changeoutToBucket,
              observations: data.inspectionCategoryTypes[0].observations,
              effectiveDate: data.inspectionDate,
            });
        }

        equipmentList.push(changeoutToBucket);

        const changeoutBucket = currentBucket.unassignedEquipment.find(
          (eq) => eq.externalId === changeoutFromObservation.value,
        );
        if (changeoutBucket) {
          equipmentList.push(changeoutBucket);
        }
      }
    } else if (currentBucket.bucket) {
      if (currentBucket.bucket.id < 1) {
        const observationsBefore = copyObservations(data);
        const response =
          await assetMethods.createNewBucketFromEquipmentAndObservations({
            bucket: currentBucket.bucket,
            observations: data.inspectionCategoryTypes[0].observations,
            effectiveDate: data.inspectionDate,
          });
        currentBucket.bucket = response.bucket;
        data = await inspectionDB.transaction(
          'rw',
          inspectionDB.inspections,
          inspectionDB.inspectionBackups,
          inspectionDB.equipmentParts,
          async () => {
            const updated =
              await inspectionRecordService.applyObservationRewrite(
                externalId,
                observationsBefore,
                response.observations,
              );
            await equipmentPartsMethods.replaceEquipmentEquipmentParts({
              equipmentExternalId: response.bucket.externalId,
              equipmentParts: response.bucket.equipmentParts,
            });
            return updated;
          },
        );
      } else {
        currentBucket.bucket =
          await assetMethods.joinBucketEquipmentPartsObservationData({
            bucket: currentBucket.bucket,
            observations: data.inspectionCategoryTypes[0].observations,
            effectiveDate: data.inspectionDate,
          });
      }

      equipmentList.push(currentBucket.bucket);
    }

    // #10363: fail closed before the POST if any equipment item would be rejected
    // by the server's exactly-one-of(assetId, mineLocationId) rule - typically an
    // outgoing bucket with no laydown yard to move to. The inspection stays on the
    // device; retrying won't help, so mark fatal to keep the online auto-retry
    // loop from spinning on it.
    const invalidEquipment = findInvalidEquipmentAssignments(equipmentList);
    if (invalidEquipment.length > 0) {
      const errorMsg = buildInvalidEquipmentSubmitMessage(invalidEquipment);
      console.error(
        'Blocked inspection submit: invalid equipment assignment',
        invalidEquipment,
      );
      return failed(errorMsg, { fatal: true });
    }

    // #10207: observations recorded against the changeout-to bucket are submitted
    // with entityId -1 (see resolveSubmitEntityId) so the server accepts the pending
    // reassignment and maps them to the new equipment row.
    const changeoutBucketExternalId =
      currentBucket.isChanged && currentBucket.bucket
        ? currentBucket.bucket.externalId
        : null;

    // ---- Typed change-out statements (#10223 native adoption) ----
    // The server no longer distils Operation.UndercarriageChangeOut.* observations;
    // the checked side families are serialised into the typed contract here instead.
    const crawlerTypeIds = new Set(
      (await inspectionDB.equipmentTypes.toArray())
        .filter((type) => type.name === 'Crawler')
        .map((type) => type.id),
    );
    const crawlerSides =
      data.assetId > 0
        ? (
            await inspectionDB.equipments
              .where('assetId')
              .equals(data.assetId)
              .toArray()
          ).filter((equipment) => crawlerTypeIds.has(equipment.equipmentTypeId))
        : [];
    const undercarriageChangeOuts = buildUndercarriageChangeOutSyncItems({
      observations: data.inspectionCategoryTypes[0].observations,
      crawlerSides,
    });

    // Per-position GET replacements: statements are keyed to equipment parts, so the
    // lookup runs AFTER the bucket-creation flows above rewrote any provisional ids.
    const equipmentPartsByExternalId = new Map(
      [
        ...(data.fitmentSnapshots ?? []),
        ...(await inspectionDB.equipmentParts.toArray()),
      ].map((part) => [part.externalId, part]),
    );
    const partIdByExternalId = new Map(
      (await inspectionDB.parts.toArray())
        .filter((part) => part.id > 0)
        .map((part) => [part.externalId, part.id]),
    );
    const getChangeOuts = buildGetChangeOutSyncItems({
      observations: data.inspectionCategoryTypes[0].observations,
      equipmentPartsByExternalId,
      partIdByExternalId,
      inspectionDate: data.inspectionDate,
    });

    // ---- Build payload ----
    const payload: SaveInspectionsOperationRequest = {
      saveInspectionsRequest: {
        syncDetails: {
          lastSyncDate: localStorage.getItem('lastSyncDate')
            ? new Date(localStorage.getItem('lastSyncDate') as string)
            : null,
          // The typed-contract handshake: the server 426es anything below 2.
          payloadVersion: 2,
        },
        inspections: [
          {
            inspectionId: data.id,
            externalId: data.externalId,
            categoriesTypes: data.inspectionCategoryTypes.map((category) => ({
              ...category,
              categoryTypeId: category.id,
              observations: sanitizeObservationsForWire(
                category.observations,
              ).map((obs) => ({
                ...obs,
                entityId: resolveSubmitEntityId({
                  entityId: obs.entityId,
                  dataLevelId: obs.dataLevelId,
                  entityExternalId: obs.entityExternalId,
                  changeoutBucketExternalId,
                }),
                dataTypeId: obs.observableItem.dataTypeId,
              })),
            })),
            isNew: data.id === 0 ? true : undefined,
            assetId: data.assetId,
            mineLocationId: data.mineLocationId,
            inspectionDate: data.inspectionDate,
            siteId: data.siteId,
          },
        ],
        equipmentItems: equipmentList.map((equipment) => ({
          ...equipment,
          id: equipment.id < 0 ? 0 : equipment.id,
          manufacturerId:
            equipment.manufacturerId < 1 ? 4 : equipment.manufacturerId,
        })),
        undercarriageChangeOuts,
        getChangeOuts,
      },
    };

    const result = await api.saveInspectionsRaw(payload);
    const v = await result.value();

    // Refresh any related queries
    void queryClient.invalidateQueries({
      queryKey: ['inspection', externalId],
    });

    // `data` is what was sent, after any bucket-creation or GET-part-changeout
    // transform above (#10553). The result is applied to the current record, which may
    // have been reopened or edited during the send (#11590).
    const updated = await inspectionRecordService.applySendResult(
      externalId,
      data,
      v.syncItems ?? undefined,
    );
    if (!updated) {
      return failed('Inspection not found locally');
    }

    return { inspection: updated, failure: null, fatal: false };
  } catch (err) {
    console.error('Inspection sync error:', err);
    if (err instanceof ResponseError && err.response.status === 426) {
      // This app build predates the server's sync contract: check for the update now
      // instead of waiting for the next poll. The work stays queued and sends after it.
      void updateService.checkForUpdate();
    }
    const { reason, fatal } = await describePushError(err);
    return failed(reason, { counted: isCountedFailure(err), fatal });
  }
}

/** What a thrown push error tells the inspector, and whether retrying is pointless. */
async function describePushError(
  err: unknown,
): Promise<{ reason: string; fatal: boolean }> {
  if (!(err instanceof ResponseError)) {
    return { reason: (err as Error).message, fatal: false };
  }
  if (err.response.status === 426) {
    return { reason: await upgradeRequiredMessage(err.response), fatal: true };
  }
  if (err.response.status === 403) {
    return {
      reason:
        'You do not have permission to submit this inspection. Please contact your administrator to verify your role.',
      fatal: true,
    };
  }
  try {
    const body = await err.response.json();
    if (body?.errors) {
      const errors = Object.entries(body.errors)
        .flatMap(([field, msgs]) =>
          Array.isArray(msgs) ? msgs.map((m) => `${field}: ${m}`) : [],
        )
        .join(', ');
      return { reason: `Validation errors: ${errors}`, fatal: false };
    }
    if (typeof body === 'string') {
      return { reason: `API error: ${body}`, fatal: false };
    }
    return { reason: 'Unexpected API error', fatal: false };
  } catch {
    return {
      reason: `API error: ${err.response.status} ${err.response.statusText}`,
      fatal: false,
    };
  }
}

/** The server's message for a 426 UpgradeRequired, or a default one. */
async function upgradeRequiredMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  } catch (error) {
    // The status alone says the app is too old; the body only words it. A body that
    // isn't JSON, is empty or breaks off while read gets the default text, so the push
    // still returns its failure instead of throwing out of its own catch.
    if (!(error instanceof SyntaxError)) {
      console.warn('Could not read the 426 response body:', error);
    }
  }
  return 'This version of the Insite Inspection app can no longer sync. Please apply the app update and try again - your captured work is preserved.';
}

export const inspectionPushService = new InspectionPushService(
  pushInspectionOnce,
);
